import React from 'react';
import clsx from 'clsx';
import { Box, Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';
import { LedgerTiles } from '../../types';

const useStyles = makeStyles()((theme: Theme) => ({
    summaryCardsContainer: {
        // Grid keeps every card the same width; when they wrap, a leftover card does not stretch to full width
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '16px',
        marginBottom: theme.spacing(2),
    },

    summaryCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        borderRadius: '12px',
        padding: '16px 20px',
        border: `1px solid ${theme.palette.secondary.dark}`,
        background: theme.palette.primary.light,
    },
    commonText: {
        color: theme.palette.common.white,
        fontWeight: 400,
        lineHeight: 'normal',
        fontStyle: 'normal',
        fontFamily: 'Inter',
    },
    title: {
        fontSize: '14px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    value: {
        fontSize: '28px',
        fontWeight: 700,
    },
    detailText: {
        fontSize: '14px',
    },
    textGrayColor: {
        color: theme.palette.text.secondary,
    },
    creditDebitRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
    },
}));

type CaseLedgerSummaryCardsProps = {
    tiles: LedgerTiles;
};

const formatSignedCurrency = (value: number) =>
    `${value >= 0 ? '+' : '-'}${numberToUSDCurrency(Math.abs(value))}`;

const CaseLedgerSummaryCards: React.FC<CaseLedgerSummaryCardsProps> = ({ tiles }) => {
    const { classes } = useStyles();

    const detailClass = clsx(classes.commonText, classes.detailText, classes.textGrayColor);

    const renderCard = (
        title: string,
        value: string,
        credits: number | null,
        debits: number | null,
        footer?: string,
    ) => (
        <Box className={classes.summaryCard}>
            <Typography mb={1} className={clsx(classes.commonText, classes.title, classes.textGrayColor)}>
                {title}
            </Typography>

            <Typography mb={1} className={clsx(classes.commonText, classes.value)}>{value}</Typography>

            <Box mb={1} className={classes.creditDebitRow}>
                <Typography className={detailClass}>Credits {numberToUSDCurrency(credits)}</Typography>
                <Typography className={detailClass}>Debits {numberToUSDCurrency(debits)}</Typography>
            </Box>

            {footer && <Typography mb={1} className={detailClass}>{footer}</Typography>}
        </Box>
    );

    return (
        <Box className={classes.summaryCardsContainer}>
            {renderCard(
                'Posted Balance',
                numberToUSDCurrency(tiles.posted.balance),
                tiles.posted.credits,
                tiles.posted.debits,
            )}
            {renderCard(
                'Funds in Transit',
                formatSignedCurrency(tiles.inTransit.balance),
                tiles.inTransit.credits,
                tiles.inTransit.debits,
            )}
            {renderCard(
                'Projected Balance',
                numberToUSDCurrency(tiles.projected.balance),
                tiles.projected.credits,
                tiles.projected.debits,
                `${tiles.projected.pendingLegs} legs pending`,
            )}
        </Box>
    );
};

export default CaseLedgerSummaryCards;
